import { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { getApiError } from '@/api/helpers/apiError'
import {
  useGetCameraDetails,
  useRefreshCameraImage
} from '@/queries/cameraQueries'
import { usePollTaskStatus } from '@/queries/generalQueries'
import { showToast } from '@/theme/notifications'

type UseCameraImageParams = {
  deviceId: string
  cameraStreamId: string
  enabled: boolean
}

export const useCameraImage = ({
  deviceId,
  cameraStreamId,
  enabled
}: UseCameraImageParams) => {
  const intl = useIntl()
  const [refreshImageTaskId, setRefreshImageTaskId] = useState<string>('')

  const {
    data: cameraStreamData,
    refetch,
    isFetching: isFetchingCameraDetails
  } = useGetCameraDetails({
    deviceId,
    cameraId: cameraStreamId,
    enabled
  })

  const { mutateAsync: refreshImage, isPending: isPendingImageRefresh } =
    useRefreshCameraImage()

  const { data: refreshImageTaskData, isLoading: isLoadingTaskStatus } =
    usePollTaskStatus(refreshImageTaskId, refreshImageTaskId !== '')

  useEffect(() => {
    if (refreshImageTaskData?.task_status === 'SUCCESS') {
      void refetch()
      setRefreshImageTaskId('')
    }

    if (refreshImageTaskData?.task_status === 'FAILURE') {
      showToast(
        intl.formatMessage({ id: 'cameras.image.refresh.error' }),
        'error'
      )
      setRefreshImageTaskId('')
    }
  }, [refreshImageTaskData, refetch, intl])

  const isImageLoading =
    isFetchingCameraDetails ||
    isPendingImageRefresh ||
    isLoadingTaskStatus ||
    refreshImageTaskData?.task_status === 'PENDING'

  const handleRefresh = async () => {
    try {
      const { task_id } = await refreshImage({
        deviceId,
        cameraId: cameraStreamId
      })
      setRefreshImageTaskId(task_id)
    } catch (err) {
      const { errorMessage } = getApiError(err)
      const message =
        errorMessage ||
        intl.formatMessage({ id: 'cameras.image.refresh.error' })
      showToast(message, 'error')
    }
  }

  return {
    isImageLoading,
    cameraImage: cameraStreamData?.image,
    refreshCameraImage: handleRefresh
  }
}
